import { z } from 'zod';
/**
 * Geldmodell von caterinx — verbindlich, siehe `docs/02_DOMAIN_MODEL.md` §3.
 *
 * Drei Regeln, aus denen sich alles andere ergibt:
 *
 * 1. **Alles ist Integer-Cent.** Kein `number` mit Nachkommastellen, nirgends. Eine
 *    Fließkommazahl, die einmal `0.1 + 0.2` gesehen hat, kommt nicht mehr sauber zurück.
 * 2. **Der USt-Satz hängt an der Position, nicht an einer globalen Konstante.** Catering ist in
 *    Deutschland 7 % (reine Speisenlieferung) oder 19 % (Restaurationsleistung mit Personal und
 *    Geschirr). Der Lieferant kennt seinen Fall — der Code darf ihn nicht raten.
 * 3. **Gerundet wird an definierten Stellen, und die Teile summieren sich exakt zum Ganzen.**
 *    Zwischensummen werden nie gerundet. Wo eine gerundete Gesamtsumme auf Teile verteilt wird,
 *    passiert das über {@link allocate} — nicht durch erneutes Runden je Teil.
 *
 * Warum BigInt statt `Math.round(net * bp / 10000)`: die Formel aus dem Domain-Model ist für
 * realistische Beträge korrekt, aber ihre Korrektheit hängt daran, dass das Zwischenprodukt
 * unter 2^53 bleibt. Bei der Verteilung einer Provision auf USt-Gruppen (`amount * weight`) ist
 * das nicht mehr garantiert. Statt zwei Rundungspfade mit unterschiedlichen Garantien zu pflegen,
 * rechnet diese Datei durchgängig exakt. Für nicht-negative Beträge liefert
 * {@link roundHalfUp} bitgleich dasselbe wie `Math.round` — die Tests prüfen genau das.
 */
/** Nur EUR im MVP. Als Schema, damit das Feld später ohne Migration erweiterbar ist. */
export const CurrencySchema = z.literal('EUR');
/**
 * USt-Satz in Basispunkten: 1900 = 19 %, 700 = 7 %, 0 = steuerfrei.
 * Basispunkte statt Prozent, damit auch 8,5 % ohne Nachkommastelle darstellbar bleibt.
 */
export const VatRateBpSchema = z.int().min(0).max(10000);
/** Regelsteuersatz — Restaurationsleistung (mit Personal, Geschirr, Service). */
export const VAT_STANDARD_BP = 1900;
/** Ermäßigter Satz — reine Speisenlieferung ohne Dienstleistungselement. */
export const VAT_REDUCED_BP = 700;
/**
 * Auf der Provisionsrechnung liegen immer 19 %: B2B-Vermittlungsleistung im Inland.
 * (Reverse Charge bei ausländischen Lieferanten ist im MVP nicht vorgesehen — das Feld
 * `vatRateBp` auf der Commission erlaubt es später ohne Migration.)
 */
export const COMMISSION_VAT_BP = VAT_STANDARD_BP;
/** Plattform-Default für die Provision: 10 %. Pro Lieferant überschreibbar (D16). */
export const DEFAULT_COMMISSION_RATE_BP = 1000;
/**
 * Obergrenze für jeden Einzelbetrag: 100 Mio EUR. Kein fachliches Limit, sondern eine
 * Reißleine — ein Betrag darüber ist ein Eingabe- oder Einheitenfehler (Euro statt Cent),
 * und der soll laut scheitern statt still eine Rechnung zu erzeugen.
 */
export const MAX_AMOUNT_CENTS = 10_000_000_000;
/** Ein Betrag, wie er gespeichert wird. `grossCents` ist abgeleitet, aber persistiert (Audit). */
export const MoneySchema = z.object({
    netCents: z.int().min(0).max(MAX_AMOUNT_CENTS),
    vatRateBp: VatRateBpSchema,
    grossCents: z.int().min(0),
    currency: CurrencySchema,
});
/** Eine Angebotsposition. Der USt-Satz sitzt hier — bewusst, siehe Regel 2 oben. */
export const LineItemSchema = z.object({
    netCents: z.int().min(0).max(MAX_AMOUNT_CENTS),
    vatRateBp: VatRateBpSchema,
});
/* --------------------------------------------------------------------------------------------
 * Wachen
 * ----------------------------------------------------------------------------------------- */
/** Wirft, wenn `value` kein nicht-negativer Integer-Cent-Betrag im erlaubten Rahmen ist. */
export function assertCents(value, label = 'Betrag') {
    if (!Number.isInteger(value)) {
        throw new RangeError(`${label} muss ein Integer-Cent-Wert sein, war: ${value}`);
    }
    if (value < 0) {
        throw new RangeError(`${label} darf nicht negativ sein, war: ${value}`);
    }
    if (value > MAX_AMOUNT_CENTS) {
        throw new RangeError(`${label} überschreitet ${MAX_AMOUNT_CENTS} Cent (Einheitenfehler?), war: ${value}`);
    }
}
/** Wirft, wenn `value` kein gültiger Basispunkt-Satz ist (0…10000). */
export function assertRateBp(value, label = 'Satz') {
    if (!Number.isInteger(value)) {
        throw new RangeError(`${label} muss ein Integer in Basispunkten sein, war: ${value}`);
    }
    if (value < 0 || value > 10000) {
        throw new RangeError(`${label} muss zwischen 0 und 10000 Basispunkten liegen, war: ${value}`);
    }
}
/* --------------------------------------------------------------------------------------------
 * Rundung
 * ----------------------------------------------------------------------------------------- */
/**
 * Kaufmännische Rundung von `numerator / denominator` — exakt, ohne Fließkomma.
 *
 * `.5` geht nach oben (kaufmännisch, und identisch zu `Math.round` für nicht-negative Werte).
 * Der Trick ist `(2n + d) / 2d` in Integer-Arithmetik: das verschiebt den Rundungspunkt genau um
 * eine halbe Einheit, danach genügt Abschneiden.
 *
 * @internal Exportiert nur, damit die Tests die Äquivalenz zu `Math.round` belegen können.
 */
export function roundHalfUp(numerator, denominator) {
    if (denominator <= 0n) {
        throw new RangeError(`Nenner muss positiv sein, war: ${denominator}`);
    }
    if (numerator < 0n) {
        throw new RangeError(`Zähler darf nicht negativ sein, war: ${numerator}`);
    }
    const result = (2n * numerator + denominator) / (2n * denominator);
    return Number(result);
}
/**
 * Verteilt `totalCents` auf Teile im Verhältnis von `weights` — **ohne Rundungsverlust**.
 *
 * Das ist die Funktion, die den klassischen Ein-Cent-Fehler verhindert: Wer jeden Teil einzeln
 * rundet, bekommt eine Summe, die um ein bis zwei Cent neben dem Gesamtbetrag liegt. Hier wird
 * jeder Teil abgerundet und der verbleibende Rest nach der Größe der Nachkommaanteile verteilt
 * (Hare-Niemeyer / largest remainder). Ergebnis: `sum(allocate(t, w)) === t`, immer.
 *
 * Bei Gleichstand der Reste gewinnt das größere Gewicht, danach der kleinere Index — damit ist
 * das Ergebnis deterministisch und in Tests reproduzierbar.
 *
 * @param totalCents zu verteilender Gesamtbetrag (nicht negativ)
 * @param weights    Gewichte, je nicht negativ. Summe 0 ist nur mit `totalCents === 0` erlaubt.
 */
export function allocate(totalCents, weights) {
    assertCents(totalCents, 'Verteilbetrag');
    for (const w of weights) {
        if (!Number.isInteger(w) || w < 0) {
            throw new RangeError(`Gewicht muss ein nicht-negativer Integer sein, war: ${w}`);
        }
    }
    if (weights.length === 0) {
        if (totalCents !== 0) {
            throw new RangeError(`Es gibt keine Empfänger, aber ${totalCents} Cent zu verteilen`);
        }
        return [];
    }
    const total = BigInt(totalCents);
    const bigWeights = weights.map((w) => BigInt(w));
    const weightSum = bigWeights.reduce((a, b) => a + b, 0n);
    if (weightSum === 0n) {
        if (totalCents !== 0) {
            throw new RangeError(`Gewichtssumme ist 0, aber es sind ${totalCents} Cent zu verteilen`);
        }
        return weights.map(() => 0);
    }
    // Abrunden je Teil, Rest merken.
    const base = [];
    const remainders = [];
    let distributed = 0n;
    for (const [index, weight] of bigWeights.entries()) {
        const product = total * weight;
        const share = product / weightSum;
        base.push(share);
        distributed += share;
        remainders.push({ index, remainder: product % weightSum, weight });
    }
    // Restliche Cents nach größtem Nachkommaanteil vergeben.
    let leftover = total - distributed;
    remainders.sort((a, b) => {
        if (a.remainder !== b.remainder)
            return a.remainder > b.remainder ? -1 : 1;
        if (a.weight !== b.weight)
            return a.weight > b.weight ? -1 : 1;
        return a.index - b.index;
    });
    for (const entry of remainders) {
        if (leftover <= 0n)
            break;
        base[entry.index] += 1n;
        leftover -= 1n;
    }
    return base.map((v) => Number(v));
}
/* --------------------------------------------------------------------------------------------
 * Grundrechenarten auf Beträgen
 * ----------------------------------------------------------------------------------------- */
/** USt-Betrag zu einem Nettobetrag. Rundet kaufmännisch. */
export function vatFromNet(netCents, vatRateBp) {
    assertCents(netCents, 'Nettobetrag');
    assertRateBp(vatRateBp, 'USt-Satz');
    return roundHalfUp(BigInt(netCents) * BigInt(vatRateBp), 10000n);
}
/** Bruttobetrag zu einem Nettobetrag. */
export function grossFromNet(netCents, vatRateBp) {
    return netCents + vatFromNet(netCents, vatRateBp);
}
/** Baut ein vollständiges {@link Money}-Objekt aus Netto und USt-Satz. */
export function money(netCents, vatRateBp) {
    assertCents(netCents, 'Nettobetrag');
    assertRateBp(vatRateBp, 'USt-Satz');
    return {
        netCents,
        vatRateBp,
        grossCents: grossFromNet(netCents, vatRateBp),
        currency: 'EUR',
    };
}
/**
 * Summiert Positionen **exakt** und schlüsselt nach USt-Satz auf.
 *
 * Die USt wird je Satz **einmal** auf die Gruppensumme gerechnet, nicht je Position. Sonst
 * summieren sich bis zu einem halben Cent Rundungsfehler pro Position auf, und die Rechnung
 * stimmt bei zwanzig Positionen nicht mehr mit der Summenzeile überein.
 */
export function sumLineItems(items) {
    const byRate = new Map();
    for (const item of items) {
        assertCents(item.netCents, 'Positions-Netto');
        assertRateBp(item.vatRateBp, 'Positions-USt-Satz');
        byRate.set(item.vatRateBp, (byRate.get(item.vatRateBp) ?? 0) + item.netCents);
    }
    return totalFromGroupNets(byRate);
}
/** Baut aus einer Map `vatRateBp -> netCents` die aufgeschlüsselte Summe. */
function totalFromGroupNets(byRate) {
    const groups = [...byRate.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([vatRateBp, netCents]) => {
        assertCents(netCents, 'Gruppen-Netto');
        const vatCents = vatFromNet(netCents, vatRateBp);
        return { vatRateBp, netCents, vatCents, grossCents: netCents + vatCents };
    });
    return {
        netCents: groups.reduce((sum, g) => sum + g.netCents, 0),
        vatCents: groups.reduce((sum, g) => sum + g.vatCents, 0),
        grossCents: groups.reduce((sum, g) => sum + g.grossCents, 0),
        currency: 'EUR',
        groups,
    };
}
/* --------------------------------------------------------------------------------------------
 * Provision
 * ----------------------------------------------------------------------------------------- */
/**
 * Provision auf eine Angebotssumme. **Die eine autoritative Rundung** im System.
 *
 * Bemessungsgrundlage ist immer die Gesamtsumme des Angebots bei Zuschlag (D10) — nicht die
 * Summe von je Position gerechneten Provisionen. Nachträgliche Erhöhungen der Auftragssumme
 * bleiben provisionsfrei.
 */
export function commissionNet(offerNetCents, commissionRateBp) {
    assertCents(offerNetCents, 'Angebotssumme');
    assertRateBp(commissionRateBp, 'Provisionssatz');
    return roundHalfUp(BigInt(offerNetCents) * BigInt(commissionRateBp), 10000n);
}
/**
 * Die Provisionsrechnung an den Lieferanten: Nettoprovision plus 19 % USt.
 */
export function commissionInvoiceAmount(offerNetCents, commissionRateBp) {
    return money(commissionNet(offerNetCents, commissionRateBp), COMMISSION_VAT_BP);
}
/**
 * Rechnet ein Angebot in die drei Zahlen um, die D10 verlangt: Lieferantenpreis, Kundenpreis,
 * Provision.
 *
 * Der Kundenpreis entsteht, indem die **einmal gerundete** Gesamtprovision über {@link allocate}
 * auf die USt-Gruppen verteilt und dort zum Netto addiert wird. Damit gilt beides gleichzeitig:
 *
 * - `customer.netCents === supplier.netCents + commission.netCents` (auf den Cent, immer)
 * - jede USt-Gruppe trägt ihren proportionalen Anteil, die Steuermischung bleibt erhalten
 *
 * Der naive Weg — Provision je Gruppe rechnen und runden — verletzt die erste Zusage bei
 * gemischten Sätzen regelmäßig um einen Cent. Genau das ist der Fehler, der später in der
 * Buchhaltung auffällt und nicht mehr zuzuordnen ist.
 */
export function priceOffer(items, commissionRateBp) {
    assertRateBp(commissionRateBp, 'Provisionssatz');
    const supplier = sumLineItems(items);
    const commissionNetCents = commissionNet(supplier.netCents, commissionRateBp);
    const weights = supplier.groups.map((g) => g.netCents);
    const uplift = allocate(commissionNetCents, weights);
    const customerNets = new Map();
    supplier.groups.forEach((group, index) => {
        // eslint-disable-next-line security/detect-object-injection -- `index` ist der Schleifenzähler; `uplift` hat per Konstruktion dieselbe Länge wie `groups`
        customerNets.set(group.vatRateBp, group.netCents + uplift[index]);
    });
    // Ein Angebot ohne Positionen hat kein Netto und damit auch keine USt-Gruppe. Der Kundenpreis
    // ist dann ebenfalls null — `allocate` hat oben bereits sichergestellt, dass in diesem Fall
    // nichts zu verteilen war.
    const customer = totalFromGroupNets(customerNets);
    return {
        supplier,
        customer,
        commission: money(commissionNetCents, COMMISSION_VAT_BP),
        commissionRateBp,
    };
}
/* --------------------------------------------------------------------------------------------
 * Darstellung
 * ----------------------------------------------------------------------------------------- */
/**
 * Formatiert Cent als deutschen Euro-Betrag: `123456` → `"1.234,56 €"`.
 *
 * Bewusst ohne `Intl.NumberFormat`-Instanz pro Aufruf (die ist teuer) und ohne Division durch
 * 100 als Fließkomma — Ganzzahl-Division plus Rest.
 */
export function formatEur(cents, options = {}) {
    assertCents(cents, 'Anzeigebetrag');
    const withSymbol = options.withSymbol ?? true;
    const euros = Math.trunc(cents / 100);
    const rest = cents % 100;
    const eurosFormatted = euros.toLocaleString('de-DE');
    const restFormatted = rest.toString().padStart(2, '0');
    return `${eurosFormatted},${restFormatted}${withSymbol ? ' €' : ''}`;
}
/** `1900` → `"19 %"`, `750` → `"7,5 %"`. */
export function formatVatRate(vatRateBp) {
    assertRateBp(vatRateBp, 'USt-Satz');
    const whole = Math.trunc(vatRateBp / 100);
    const fraction = vatRateBp % 100;
    if (fraction === 0)
        return `${whole} %`;
    const fractionText = fraction.toString().padStart(2, '0').replace(/0$/, '');
    return `${whole},${fractionText} %`;
}
